import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Skeleton, Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { useTranslation } from '@root/shared/localization'

import { ReviewBlock, ReviewBlockLabel } from './common'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: TransferFlowReatom
} & ClassNameProps

export const TransferTotals: React.FC<Props> = ({ className, model }) => {
  const { t } = useTranslation()

  const [currency] = useAtom(model.currency.currencyAtom)
  const [totalAmount] = useAtom(model.totalTransferAmountAtom)
  const [totalAmountUsd] = useAtom(model.totalTransferAmountUsdAtom)
  const [maxTotalAmount] = useAtom(model.maxTotalTransferAmountAtom)

  return (
    <Root className={className}>
      <Start>
        <TotalLabel>{t('common.total')}:</TotalLabel>
        {CurrencyModel.isEthOrWeth(currency) ? (
          <Label>{t('transfer.amount-plus-gas-fee')}</Label>
        ) : undefined}
      </Start>
      <End>
        <AmountContainer>
          <AmountUsd>
            {totalAmountUsd ? (
              <Numeric
                value={totalAmountUsd}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              <Skeleton height={17} paddingBlock={2} width={30} />
            )}
          </AmountUsd>
          <Amount>
            {totalAmount ? (
              <Numeric
                value={totalAmount}
                preset="currency-amount-short"
                currency={currency.ticker}
              />
            ) : (
              <Skeleton height={17} paddingBlock={2} width={60} />
            )}
          </Amount>
        </AmountContainer>
        {CurrencyModel.isEthOrWeth(currency) ? (
          <MaxAmount>
            <span>{t('transfer.max-amount')}: </span>
            {maxTotalAmount ? (
              <Numeric
                value={maxTotalAmount}
                preset="currency-amount-short"
                currency={currency.ticker}
              />
            ) : (
              <Skeleton height={16} paddingBlock={3} width={60} />
            )}
          </MaxAmount>
        ) : undefined}
      </End>
    </Root>
  )
}

const Root = styled(ReviewBlock)`
  padding-block: 16px 16px;
  padding-inline: 12px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
`

const Start = styled.div`
  display: grid;
`
const End = styled.div`
  display: grid;
  justify-items: end;
`

const Label = styled(Typography).attrs({
  variant: 'body5-medium',
  component: 'div',
})`
  color: var(--text-10);
  margin-bottom: 2px;
`

const AmountContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-items: end;
  gap: 8px;
`
const Amount = styled(Typography).attrs({
  variant: 'body2-bold',
  component: 'div',
})`
  color: var(--text-12);
`

const AmountUsd = styled(Typography).attrs({
  variant: 'body2-medium',
  component: 'div',
})`
  color: var(--text-10);
`

const TotalLabel = styled(ReviewBlockLabel)`
  margin-bottom: 4px;
`

const MaxAmount = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'div',
})`
  color: var(--text-10);

  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 4px;
`
